import { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from '@/context/RouterContext';

export function useWizardState<T extends Record<string, any>>(initialState: T) {
  const { path, params, navigate } = useRouter();

  // Keep a ref to the latest state so the updateState callback can read it
  // without needing `state` in its dependency array.
  const stateRef = useRef<T>(initialState);

  // Parse state from URL if present
  const decodeState = useCallback((): T => {
    if (params.state) {
      try {
        const json = atob(params.state);
        return { ...initialState, ...JSON.parse(json) };
      } catch (e) {
        console.error('Failed to parse wizard state from URL', e);
      }
    }
    return initialState;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.state]);

  const [state, setState] = useState<T>(decodeState);
  stateRef.current = state;

  // Re-sync local state if the URL state changes (e.g. from back button)
  useEffect(() => {
    const decoded = decodeState();
    stateRef.current = decoded;
    setState(decoded);
  }, [decodeState]);

  // Update local state and push/replace to URL
  const updateState = useCallback(
    (newState: Partial<T> | ((prev: T) => Partial<T>), replace = true) => {
      const prev = stateRef.current;
      const updates = typeof newState === 'function' ? newState(prev) : newState;
      const updated = { ...prev, ...updates };

      stateRef.current = updated;
      // Update React state (pure, no side-effects)
      setState(updated);

      // Sync the updated state to the URL.
      // encodeURIComponent ensures '+' in base64 is escaped as %2B so that
      // URLSearchParams (application/x-www-form-urlencoded) does not silently
      // replace it with a space character on decode.
      const base64 = btoa(JSON.stringify(updated));
      const currentHash = window.location.hash || path;
      const [hashPath, existingQuery] = currentHash.split('?');
      const searchParams = new URLSearchParams(existingQuery || '');
      searchParams.set('state', base64);
      const newUrl = `${hashPath}?${searchParams.toString()}`;
      navigate(newUrl, replace);
    },
    [navigate, path]
  );

  return [state, updateState] as const;
}
